/**
 * EVENTSFLO-TASK-DEFINITIONS-PHASE-A-RENAME-CONFIRMATION-V1
 *
 * Settings → Task Definition Bilingual Rename Review
 *
 * Read-only by default. Lists Phase A rename candidates returned by the
 * backend, lets a tenant admin tick the ones they want to apply, and
 * triggers the apply endpoint with a confirmation dialog showing
 * before / after.
 *
 * Safety contract (also enforced server-side):
 *  - Default selection includes ONLY rows with `safety === "safe"`.
 *  - Manual-review rows must be explicitly ticked.
 *  - The dialog displays a full before → after table and dependency
 *    counts so the admin can back out if anything looks wrong.
 *  - Apply only mutates `name`; ID, rates, multiplier, tax mode, and
 *    active status are preserved.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  listBilingualRenameCandidates,
  applyBilingualRenameCandidates,
  extractErrorMessage,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Loader2, Languages, AlertTriangle, ShieldCheck, RefreshCcw } from "lucide-react";

function SafetyBadge({ safety }) {
  if (safety === "safe") {
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/40 text-emerald-200 bg-emerald-500/10"
        data-testid="phasea-badge-safe"
      >
        <ShieldCheck className="w-3 h-3 mr-1" />
        Safe rename
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-amber-500/40 text-amber-200 bg-amber-500/10"
      data-testid="phasea-badge-manual"
    >
      <AlertTriangle className="w-3 h-3 mr-1" />
      Manual review
    </Badge>
  );
}

function CandidateRow({ candidate, selected, onToggle }) {
  const hasDeps = (candidate.dependency_count || 0) > 0;
  return (
    <tr
      data-testid={`phasea-row-${candidate.task_definition_id}`}
      className="border-b border-slate-800 hover:bg-slate-800/30"
    >
      <td className="p-3">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          data-testid={`phasea-checkbox-${candidate.task_definition_id}`}
        />
      </td>
      <td className="p-3">
        <div className="font-medium text-slate-100">{candidate.current_name}</div>
        <div className="text-xs text-slate-500 font-mono mt-0.5">
          id: {candidate.task_definition_id.slice(0, 8)}…
        </div>
      </td>
      <td className="p-3">
        <div className="text-emerald-300 font-medium">{candidate.recommended_name}</div>
        <div className="text-xs text-slate-400 mt-1 max-w-md">{candidate.reason}</div>
      </td>
      <td className="p-3 text-xs text-slate-300">
        <div>charge: <span className="font-mono">{candidate.default_charge_rate}</span></div>
        <div>pay: <span className="font-mono">{candidate.default_pay_rate}</span></div>
        <div className="text-slate-500">
          mult: {candidate.multiplier_type || "—"} · tax: {candidate.tax_mode || "—"}
        </div>
      </td>
      <td className="p-3 text-xs">
        <Badge
          variant="outline"
          className={
            hasDeps
              ? "border-amber-500/40 text-amber-200 bg-amber-500/10"
              : "border-slate-600 text-slate-300"
          }
          data-testid={`phasea-deps-${candidate.task_definition_id}`}
        >
          {hasDeps
            ? `${candidate.dependency_count} live refs`
            : "no live refs"}
        </Badge>
        {hasDeps && (
          <div className="text-slate-500 mt-1">
            {Object.entries(candidate.dependency_summary || {})
              .map(([k, v]) => `${k}: ${v}`)
              .join(" · ")}
          </div>
        )}
      </td>
      <td className="p-3">
        <SafetyBadge safety={candidate.safety} />
      </td>
    </tr>
  );
}

export default function TaskDefinitionBilingualRenamePage() {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await listBilingualRenameCandidates();
      const list = data.candidates || [];
      setCandidates(list);
      // Default selection: only "safe" rows.
      const next = {};
      list.forEach((c) => {
        if (c.safety === "safe") next[c.task_definition_id] = true;
      });
      setSelected(next);
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedItems = useMemo(
    () => candidates.filter((c) => selected[c.task_definition_id]),
    [candidates, selected]
  );

  const onToggle = (id) =>
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const onApply = useCallback(async () => {
    setApplying(true);
    try {
      const items = selectedItems.map((c) => ({
        task_definition_id: c.task_definition_id,
        expected_current_name: c.current_name,
        new_name: c.recommended_name,
      }));
      const { data } = await applyBilingualRenameCandidates(items);
      const ok = (data.applied || []).length;
      const errs = (data.errors || []).length;
      if (ok && !errs) {
        toast.success(`Renamed ${ok} Task Definition${ok === 1 ? "" : "s"}.`);
      } else if (ok && errs) {
        toast.warning(`Renamed ${ok}, but ${errs} row(s) had errors. Reloading…`);
      } else {
        toast.error(
          `No renames applied. ${errs} error(s) — see console for details.`
        );
        console.warn("[phasea] errors:", data.errors);
      }
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(extractErrorMessage(e) || "Apply failed");
    } finally {
      setApplying(false);
    }
  }, [selectedItems, load]);

  return (
    <div
      className="container mx-auto px-4 py-8 max-w-6xl"
      data-testid="phasea-page"
    >
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2 text-slate-100">
          <Languages className="w-7 h-7 text-emerald-400" />
          Task Definition Bilingual Rename Review
        </h1>
        <p className="text-slate-400 mt-1 text-sm max-w-3xl">
          Phase A — <strong className="text-slate-300">rename only</strong>.
          The selected Task Definitions get a clearer bilingual EN/FR name.
          IDs, rates, supplement groups, tax mode, multipliers and every
          downstream reference (shifts, rules, quotes, invoices) are
          preserved exactly as they are. No merges, no deletes, no
          deactivations, no rate changes.
        </p>
      </div>

      <Card className="bg-slate-900/40 border-slate-800">
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle className="text-slate-100">Phase A candidates</CardTitle>
            <CardDescription className="text-slate-400 text-sm">
              Defaults select only{" "}
              <span className="text-emerald-300">Safe rename</span> rows.
              Tick a{" "}
              <span className="text-amber-300">Manual review</span> row
              only after confirming it is the canonical generic record
              (not a department-specific or route-specific variant).
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="phasea-reload"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            Reload
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading candidates…
            </div>
          ) : candidates.length === 0 ? (
            <div
              className="text-sm text-slate-400 py-6 text-center"
              data-testid="phasea-empty"
            >
              No Phase A candidates for this tenant. Either no matching
              rows exist, or all have already been renamed.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="phasea-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                    <th className="p-3 w-10"></th>
                    <th className="p-3">Current name</th>
                    <th className="p-3">Recommended bilingual name</th>
                    <th className="p-3">Rates / Multiplier</th>
                    <th className="p-3">Dependencies</th>
                    <th className="p-3">Safety</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <CandidateRow
                      key={c.task_definition_id}
                      candidate={c}
                      selected={!!selected[c.task_definition_id]}
                      onToggle={() => onToggle(c.task_definition_id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={selectedItems.length === 0 || applying}
              data-testid="phasea-apply"
            >
              Apply selected renames
              {selectedItems.length > 0 && ` (${selectedItems.length})`}
            </Button>
            <p className="text-xs text-slate-500">
              IDs are preserved. No merges, no deletes, no rate changes.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog with before / after preview */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          className="max-w-2xl"
          data-testid="phasea-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              Confirm bilingual rename
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              The following Task Definitions will be renamed. Every other
              field — including ID, rates, multipliers, tax mode, active
              status and downstream references — stays exactly as-is.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-auto rounded border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-800">
                  <th className="p-2">Before</th>
                  <th className="p-2">→</th>
                  <th className="p-2">After</th>
                </tr>
              </thead>
              <tbody>
                {selectedItems.map((c) => (
                  <tr
                    key={c.task_definition_id}
                    className="border-b border-slate-800"
                    data-testid={`phasea-confirm-row-${c.task_definition_id}`}
                  >
                    <td className="p-2 text-slate-300">{c.current_name}</td>
                    <td className="p-2 text-slate-600">→</td>
                    <td className="p-2 text-emerald-300">
                      {c.recommended_name}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setConfirmOpen(false)}
              disabled={applying}
            >
              Cancel
            </Button>
            <Button
              onClick={onApply}
              disabled={applying}
              data-testid="phasea-confirm-apply"
            >
              {applying ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Confirm &amp; apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
